import React, { useState, useRef, useCallback } from 'react';
import * as tus from 'tus-js-client';
import api from '../../services/api';
import { uploadImage, uploadFile, uploadAudio } from '../../services/upload.service';

/**
 * Drag-and-drop (or click to browse) for course and lesson media — Jenn, 2026-10-26: "set up all the courses
 * (lessons, etc) so I can drag images, videos and audios where they need to go. It's an extra step when I have to
 * drag them to google and grab a share link."
 *
 * kind = 'image' | 'audio' | 'file' | 'video'. Pictures, audio and documents land on our own server and come back
 * as a link. Videos go straight from the browser to the Bunny video library (resumable, no size limit) and come
 * back as a Bunny video id; they also appear in Team365's Video Library.
 */
const ACCEPT = { image: 'image/*', audio: 'audio/*', video: 'video/*', file: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,image/*' };
const WORD = { image: 'a picture', audio: 'an audio file', video: 'a video', file: 'a file' };

export default function MediaDropZone({ kind, onUploaded, videoTitle }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  const uploadVideo = (file) => new Promise((resolve, reject) => {
    const title = (videoTitle || file.name.replace(/\.[^.]+$/, '')).trim();
    api.post('/admin/bunny-library/upload-start', { title }).then(({ data }) => {
      const up = new tus.Upload(file, {
        endpoint: data.endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
        headers: { AuthorizationSignature: data.signature, AuthorizationExpire: String(data.expire), VideoId: data.videoId, LibraryId: String(data.libraryId) },
        metadata: { filetype: file.type, title },
        onError: (e) => reject(e),
        onProgress: (sent, total) => setProgress(Math.round((sent / total) * 100)),
        onSuccess: () => resolve(data.videoId),
      });
      up.findPreviousUploads().then((prev) => { if (prev.length) up.resumeFromPreviousUpload(prev[0]); up.start(); });
    }).catch(reject);
  });

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const okType = kind === 'file' || (file.type || '').startsWith(`${kind}/`);
    if (!okType) { setError(`That isn't ${WORD[kind]}.`); return; }
    setBusy(true); setError(null); setDone(null); setProgress(kind === 'video' ? 0 : null);
    try {
      let value;
      if (kind === 'image') value = await uploadImage(file);
      else if (kind === 'audio') value = await uploadAudio(file);
      else if (kind === 'file') value = (await uploadFile(file)).url;
      else value = await uploadVideo(file);
      onUploaded(value, file);
      setDone(kind === 'video' ? `${file.name} uploaded. Bunny is preparing it; it plays once that finishes (usually a few minutes).` : `${file.name} added ✓`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || `Could not upload ${WORD[kind]}.`);
    } finally {
      setBusy(false); setProgress(null);
    }
  }, [kind, onUploaded, videoTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && inputRef.current?.click()}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
      aria-label={`Upload ${WORD[kind]}`}
      className={`mt-2 border-2 border-dashed rounded-lg px-3 py-3 text-center text-xs cursor-pointer transition-colors
        ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <input ref={inputRef} type="file" accept={ACCEPT[kind]} className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
      {busy ? (
        <span className="text-gray-500">{progress !== null ? `Uploading… ${progress}%` : 'Uploading…'}{kind === 'video' && ' (keep this page open)'}</span>
      ) : (
        <span className="text-gray-400">Drag {WORD[kind]} here, or click to choose one</span>
      )}
      {progress !== null && (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-2"><div className="h-full bg-reset-terracotta" style={{ width: `${progress}%` }} /></div>
      )}
      {done && <p className="text-reset-sage mt-1">{done}</p>}
      {error && <p className="text-red-600 mt-1">{error}</p>}
    </div>
  );
}
